import React, { useState } from 'react';
import { formatNumber, formatCurrency, getCurrencySymbol } from '../../utils/formatters';
import {
  Pencil,
  Trash2,
  PauseCircle,
  Play,
  Package,
  History,
  Search,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Plus,
  RotateCcw,
} from 'lucide-react';
import ActionMenu from '../common/ActionMenu';

const ProductsList = ({
  produits,
  filteredProduits,
  stockAlerts,
  stockMovements,
  gestionStockActive,
  settings,
  // Filtres
  searchTermProduits,
  setSearchTermProduits,
  showFinDeSaison,
  setShowFinDeSaison,
  familleFilter,
  setFamilleFilter,
  // Pagination
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  // États des modales
  showProduitForm,
  setShowProduitForm,
  showStockForm,
  setShowStockForm,
  showStockHistory,
  setShowStockHistory,
  editingProduit,
  setEditingProduit,
  expandedRows,
  toggleRowExpansion,
  // Handlers
  hasWriteAccess,
  handleProduitSubmit,
  handleStockSubmit,
  deleteProduit,
  toggleFinDeSaison,
  loadStockMovements,
  getPaginatedItems,
  Pagination
}) => {
  const resetFilters = () => {
    setSearchTermProduits('');
    setFamilleFilter('');
    setShowFinDeSaison(false);
    setCurrentPage(1);
  };

  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const ALERTS_PREVIEW_COUNT = 3;
  const visibleAlerts = alertsExpanded ? stockAlerts : stockAlerts.slice(0, ALERTS_PREVIEW_COUNT);
  const remainingAlerts = stockAlerts.length - ALERTS_PREVIEW_COUNT;

  // Liste des familles uniques
  const famillesUniques = [...new Set(produits.map(p => p.famille).filter(Boolean))];

  return (
    <div className="produits" data-testid="produits-section">
      <div className="section-header">
        <h2>Gestion des Produits</h2>
        <div className="header-buttons">
          {gestionStockActive && (
            <button
              onClick={() => setShowStockForm(true)}
              className="btn btn-secondary cpt-btn"
              data-testid="btn-add-stock"
            >
              <Package size={16} />
              <span>Ajouter Stock</span>
            </button>
          )}
          <button
            onClick={() => {
              setShowStockHistory(true);
              loadStockMovements();
            }}
            className="btn btn-secondary cpt-btn"
            data-testid="btn-stock-history"
          >
            <History size={16} />
            <span>Historique Stock</span>
          </button>
          {hasWriteAccess() && (
            <button
              onClick={() => setShowProduitForm(true)}
              className="btn btn-primary"
              data-testid="btn-new-product"
            >
              Nouveau Produit
            </button>
          )}
        </div>
      </div>

      {/* Alertes de stock — V2 compactes */}
      {gestionStockActive && stockAlerts.length > 0 && (
        <div className="stock-alerts-v2" data-testid="stock-alerts">
          <div className="stock-alerts-header">
            <div className="stock-alerts-title">
              <AlertTriangle size={18} />
              <h3>Alertes de Stock</h3>
              <span className="stock-alerts-count">{stockAlerts.length}</span>
            </div>
            {stockAlerts.length > ALERTS_PREVIEW_COUNT && (
              <button
                type="button"
                className="stock-alerts-toggle"
                onClick={() => setAlertsExpanded(!alertsExpanded)}
                data-testid="btn-toggle-alerts"
              >
                {alertsExpanded ? (
                  <>Réduire <ChevronUp size={14} /></>
                ) : (
                  <>Voir tout ({remainingAlerts} de plus) <ChevronDown size={14} /></>
                )}
              </button>
            )}
          </div>
          <div className="stock-alerts-list">
            {visibleAlerts.map((alert) => (
              <div
                key={alert.produit_id}
                className={`stock-alert-item stock-alert-${alert.niveau_alerte}`}
              >
                <div className="stock-alert-icon">
                  <AlertTriangle size={14} />
                </div>
                <div className="stock-alert-body">
                  <strong>{alert.nom_produit}</strong>
                  <span className="stock-alert-stock">
                    {formatNumber(alert.stock_actuel)} {alert.unite}
                    <span className="stock-alert-seuil">
                      / seuil : {formatNumber(alert.seuil_min)} {alert.unite}
                    </span>
                  </span>
                </div>
                <span className={`stock-alert-badge stock-alert-badge-${alert.niveau_alerte}`}>
                  {alert.niveau_alerte === 'critique' ? 'Critique' : 'Faible'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group search-group">
            <label><Search size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} /> Rechercher</label>
            <input
              type="text"
              placeholder="Nom, description, famille..."
              value={searchTermProduits}
              onChange={(e) => setSearchTermProduits(e.target.value)}
              className="search-input"
              data-testid="search-produits"
            />
          </div>
          <div className="filter-group">
            <label>Famille</label>
            <select
              value={familleFilter}
              onChange={(e) => setFamilleFilter(e.target.value)}
              className="filter-select"
              data-testid="filter-famille"
            >
              <option value="">Toutes les familles</option>
              {famillesUniques.map((famille) => (
                <option key={famille} value={famille}>{famille}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showFinDeSaison}
                onChange={(e) => setShowFinDeSaison(e.target.checked)}
              />
              Afficher fin de saison
            </label>
          </div>
          <div className="filter-group">
            <label>&nbsp;</label>
            <button
              onClick={resetFilters}
              className="btn btn-secondary btn-sm cpt-btn"
            >
              <RotateCcw size={14} /> Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Modal Ajout Stock */}
      {showStockForm && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3><Package size={18} /> Ajouter du Stock</h3>
              <button className="modal-close" onClick={() => setShowStockForm(false)}>×</button>
            </div>
            <form onSubmit={handleStockSubmit}>
              <div className="stock-form-container">
                <div className="produit-stock-ligne">
                  <div className="form-group">
                    <label>Produit</label>
                    <select name="produit_id[]" required>
                      <option value="">Sélectionnez un produit</option>
                      {produits.filter(p => !p.fin_de_saison).map(produit => (
                        <option key={produit.id} value={produit.id}>
                          {produit.nom}{produit.variete ? ` (${produit.variete})` : ''} - Stock: {formatNumber(produit.stock_actuel)} {produit.unite}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Quantité à ajouter</label>
                    <input type="number" name="quantite[]" step="0.01" min="0" required />
                  </div>
                </div>
              </div>
              
              <button type="button" className="btn btn-secondary btn-sm cpt-btn" onClick={() => {
                const container = document.querySelector('.stock-form-container');
                const newLigne = container.querySelector('.produit-stock-ligne').cloneNode(true);
                newLigne.querySelectorAll('input, select').forEach(input => input.value = '');
                container.appendChild(newLigne);
              }}>
                <Plus size={14} /> Ajouter un autre produit
              </button>
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary cpt-btn"><Package size={16} /> Ajouter le Stock</button>
                <button type="button" onClick={() => setShowStockForm(false)} className="btn btn-secondary">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Historique Stock */}
      {showStockHistory && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3><History size={18} /> Historique des Mouvements de Stock</h3>
              <button className="modal-close" onClick={() => setShowStockHistory(false)}>×</button>
            </div>
            <div className="stock-history-container">
              {stockMovements.length > 0 ? (
                <table className="stock-history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Produit</th>
                      <th>Type</th>
                      <th>Quantité</th>
                      <th>Stock Avant</th>
                      <th>Stock Après</th>
                      <th>Utilisateur</th>
                      <th>Raison</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockMovements.map(movement => (
                      <tr key={movement.id}>
                        <td>{movement.date_mouvement ? new Date(movement.date_mouvement).toLocaleDateString('fr-FR') : 'N/A'}</td>
                        <td>{movement.nom_produit}</td>
                        <td>
                          <span className={`mouvement-type ${movement.type_mouvement}`}>
                            {movement.type_mouvement === 'entree' ? '⬆️ Entrée' : '⬇️ Sortie'}
                          </span>
                        </td>
                        <td>{movement.quantite}</td>
                        <td>{movement.stock_avant}</td>
                        <td>{movement.stock_apres}</td>
                        <td>{movement.utilisateur}</td>
                        <td>{movement.raison === 'ajout_manuel' ? 'Ajout manuel' : 
                            movement.raison === 'commande' ? 'Commande' : 
                            movement.raison || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>Aucun mouvement de stock enregistré.</p>
              )}
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowStockHistory(false)} className="btn btn-secondary">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Formulaire Produit */}
      {showProduitForm && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>{editingProduit ? 'Modifier le produit' : 'Nouveau produit'}</h3>
              <button className="modal-close" onClick={() => {
                setShowProduitForm(false);
                setEditingProduit(null);
              }}>×</button>
            </div>
            <form onSubmit={handleProduitSubmit} data-testid="produit-form">
              <div className="commande-section commande-client">
                <h4 className="section-title">🥕 Informations du Produit</h4>
                <div className="form-group">
                  <label>Nom du produit *</label>
                  <input type="text" name="nom" required defaultValue={editingProduit?.nom || ''} />
                </div>
                <div className="form-group">
                  <label>Variété</label>
                  <input type="text" name="variete" defaultValue={editingProduit?.variete || ''} placeholder="Ex: Ronde, Allongée, Bio..." />
                </div>
                <div className="form-group">
                  <label className="checkbox-label" htmlFor="produit-bio-checkbox" data-testid="produit-bio-label">
                    <input
                      id="produit-bio-checkbox"
                      type="checkbox"
                      name="bio"
                      defaultChecked={!!editingProduit?.bio}
                      data-testid="produit-bio-checkbox"
                    />
                    <span>🌱 Produit issu de l&apos;agriculture biologique</span>
                  </label>
                  <small className="form-text">
                    Si coché, le logo AB apparaîtra sur les bons de livraison, factures, avoirs et étiquettes.
                  </small>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea name="description" defaultValue={editingProduit?.description || ''} />
                </div>
                <div className="form-group">
                  <label>Famille/Catégorie</label>
                  <select name="famille" defaultValue={editingProduit?.famille || ''}>
                    <option value="">Sélectionnez une famille</option>
                    <option value="Légumes racines">Légumes racines</option>
                    <option value="Légumes feuilles">Légumes feuilles</option>
                    <option value="Légumes fruits">Légumes fruits</option>
                    <option value="Aromates">Aromates</option>
                    <option value="Fruits">Fruits</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
              </div>

              <div className="commande-section commande-client">
                <h4 className="section-title">💰 Prix et Unité</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Prix unitaire par défaut ({getCurrencySymbol(settings?.devise)}) *</label>
                    <input type="text" inputMode="decimal" name="prix_unitaire" required
                      pattern="^-?\d+([.,]\d+)?$"
                      defaultValue={editingProduit?.prix_unitaire ?? ''}
                      placeholder="ex: 1,50 ou 1.50"
                      data-testid="produit-prix-unitaire" />
                  </div>
                  <div className="form-group">
                    <label>Unité *</label>
                    <select name="unite" required defaultValue={editingProduit?.unite || 'kg'}>
                      <option value="kg">Kilogramme (kg)</option>
                      <option value="piece">Pièce</option>
                      <option value="botte">Botte</option>
                      <option value="barquette">Barquette</option>
                      <option value="litre">Litre</option>
                    </select>
                  </div>
                </div>

                {/* Prix spécifiques par type de client — utilisés automatiquement lors de la création
                    d'une commande selon le type du client sélectionné. Laisser vide = utiliser le prix par défaut. */}
                <div className="prix-par-type" data-testid="prix-par-type-section">
                  <h5 className="prix-par-type-title">Prix spécifiques par type de client <small>(optionnel)</small></h5>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Prix {settings?.types_clients?.[0] || 'GMS'} ({getCurrencySymbol(settings?.devise)})</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        name="prix_gms"
                        pattern="^-?\d+([.,]\d+)?$"
                        defaultValue={editingProduit?.prix_par_type?.prix_gms ?? ''}
                        placeholder="Prix par défaut si vide (1,50 ou 1.50)"
                        data-testid="produit-prix-gms"
                      />
                    </div>
                    <div className="form-group">
                      <label>Prix {settings?.types_clients?.[1] || 'Boutique'} ({getCurrencySymbol(settings?.devise)})</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        name="prix_boutique"
                        pattern="^-?\d+([.,]\d+)?$"
                        defaultValue={editingProduit?.prix_par_type?.prix_boutique ?? ''}
                        placeholder="Prix par défaut si vide (1,50 ou 1.50)"
                        data-testid="produit-prix-boutique"
                      />
                    </div>
                    <div className="form-group">
                      <label>Prix {settings?.types_clients?.[2] || 'Particulier'} ({getCurrencySymbol(settings?.devise)})</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        name="prix_particulier"
                        pattern="^-?\d+([.,]\d+)?$"
                        defaultValue={editingProduit?.prix_par_type?.prix_particulier ?? ''}
                        placeholder="Prix par défaut si vide (1,50 ou 1.50)"
                        data-testid="produit-prix-particulier"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {gestionStockActive && (
                <div className="commande-section commande-client">
                  <h4 className="section-title">📦 Gestion des Stocks</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Stock actuel</label>
                      <input type="number" name="stock_actuel" step="0.01" min="0" 
                        defaultValue={editingProduit?.stock_actuel || 0} />
                    </div>
                    <div className="form-group">
                      <label>Seuil d'alerte minimum</label>
                      <input type="number" name="seuil_alerte_min" step="0.01" min="0" 
                        defaultValue={editingProduit?.seuil_alerte_min || 10} />
                    </div>
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingProduit ? 'Modifier' : 'Créer'}
                </button>
                <button type="button" onClick={() => {
                  setShowProduitForm(false);
                  setEditingProduit(null);
                }} className="btn btn-secondary">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table des produits */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th className="table-hide-mobile"></th>
              <th>Produit</th>
              <th className="table-hide-mobile">Famille</th>
              <th>Prix</th>
              {gestionStockActive && <th>Stock</th>}
              <th className="table-hide-mobile">Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedItems(filteredProduits, currentPage, itemsPerPage).map(produit => (
              <React.Fragment key={produit.id}>
                <tr className={produit.fin_de_saison ? 'fin-de-saison' : ''} data-testid={`produit-row-${produit.id}`}>
                  <td className="table-hide-mobile">
                    <button
                      className={`mobile-details-btn ${expandedRows.has(produit.id) ? 'expanded' : ''}`}
                      onClick={() => toggleRowExpansion(produit.id)}
                    >
                      {expandedRows.has(produit.id) ? '−' : '+'}
                    </button>
                  </td>
                  <td>
                    <strong>{produit.nom}</strong>
                    {produit.variete && <span className="variete-badge"> ({produit.variete})</span>}
                  </td>
                  <td className="table-hide-mobile">{produit.famille || '-'}</td>
                  <td>{formatCurrency(produit.prix_unitaire, settings?.devise)}/{produit.unite}</td>
                  {gestionStockActive && (
                    <td>
                      <span className={`stock-badge ${
                        produit.stock_actuel <= 0 ? 'stock-zero' :
                        produit.stock_actuel <= (produit.seuil_alerte_min || 10) ? 'stock-low' : 'stock-ok'
                      }`}>
                        {formatNumber(produit.stock_actuel)} {produit.unite}
                      </span>
                    </td>
                  )}
                  <td className="table-hide-mobile">
                    {produit.fin_de_saison ? (
                      <span className="status-badge fin-saison">Fin de saison</span>
                    ) : (
                      <span className="status-badge disponible">Disponible</span>
                    )}
                  </td>
                  <td>
                    {hasWriteAccess() && (
                      <ActionMenu
                        testId={`product-actions-${produit.id}`}
                        items={[
                          {
                            label: 'Modifier le produit',
                            icon: Pencil,
                            onClick: () => {
                              setEditingProduit(produit);
                              setShowProduitForm(true);
                            },
                            testId: `menu-product-edit-${produit.id}`,
                          },
                          {
                            label: produit.fin_de_saison ? 'Réactiver le produit' : 'Marquer fin de saison',
                            icon: produit.fin_de_saison ? Play : PauseCircle,
                            onClick: () => toggleFinDeSaison(produit),
                            variant: produit.fin_de_saison ? 'success' : 'warning',
                            testId: `menu-product-toggle-${produit.id}`,
                          },
                          { type: 'separator' },
                          {
                            label: 'Supprimer définitivement',
                            icon: Trash2,
                            onClick: () => deleteProduit(produit.id),
                            variant: 'danger',
                            testId: `menu-product-delete-${produit.id}`,
                          },
                        ]}
                      />
                    )}
                  </td>
                </tr>
                {/* Ligne détails mobile */}
                <tr className={`mobile-details-row ${expandedRows.has(produit.id) ? 'expanded' : ''}`}>
                  <td colSpan="7">
                    <div className="mobile-details-content">
                      <div className="mobile-details-grid">
                        <div className="mobile-detail-item">
                          <span className="mobile-detail-label">Famille</span>
                          <span className="mobile-detail-value">{produit.famille || '-'}</span>
                        </div>
                        {produit.variete && (
                          <div className="mobile-detail-item">
                            <span className="mobile-detail-label">Variété</span>
                            <span className="mobile-detail-value">{produit.variete}</span>
                          </div>
                        )}
                        <div className="mobile-detail-item">
                          <span className="mobile-detail-label">Statut</span>
                          <span className="mobile-detail-value">
                            {produit.fin_de_saison ? 'Fin de saison' : 'Disponible'}
                          </span>
                        </div>
                        {produit.description && (
                          <div className="mobile-detail-item">
                            <span className="mobile-detail-label">Description</span>
                            <span className="mobile-detail-value">{produit.description}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {filteredProduits.length === 0 && (
              <tr>
                <td colSpan="7" style={{textAlign: 'center', padding: '2rem', color: '#6c757d'}}>
                  {searchTermProduits || familleFilter ? 
                    'Aucun produit trouvé avec ces filtres' : 
                    'Aucun produit enregistré'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      <Pagination 
        items={filteredProduits}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
      />
    </div>
  );
};

export default ProductsList;
